declare global {
  interface Window {
    cheggAmplitude?: any
  }
}

export interface TrackAmplitudeEvent {
  type: string
  properties: Record<string, any>
}

/**
 * Fire an analytics event
 * @param type - The type of event to fire
 * @param properties - The properties to fire the event with
 */
export const trackAmplitudeEvent = ({ type, properties }: TrackAmplitudeEvent): void => {
  const MAX_RETRIES = 100 // 100 retries * 100ms = 10 seconds
  const RETRY_INTERVAL = 100 // 100 milliseconds

  let retryCount = 0

  const tryTrackEvent = (): void => {
    if (window.cheggAmplitude && window.cheggAmplitude.sendEvent) {
      window.cheggAmplitude.sendEvent({
        eventNameOrEvent: type,
        eventProperties: properties,
      })
    } else if (retryCount < MAX_RETRIES) {
      retryCount += 1
      setTimeout(tryTrackEvent, RETRY_INTERVAL)
    }
  }

  tryTrackEvent()
}

export const trackAmplitudeViewEvent = ({
  properties,
}: TrackAmplitudeEvent['properties']) => {
  trackAmplitudeEvent({
    type: 'component_viewed',
    properties,
  })
}

/**
 * Tracks a purchase_completed event to Amplitude with detailed purchase information
 * @param purchaseData - The ClickstreamPurchase data from the checkout callback
 */
export const trackAmplitudePurchaseCompletedEvent = (
  purchaseData: any // ClickstreamPurchase format from checkout callback
): void => {
  try {
    // Extract data with fallbacks for missing information
    const orderId = (purchaseData as any)?.data?.order?.subscription_id || ''
    const paymentMethod = (purchaseData as any)?.data?.purchase?.payments?.[0] || ''
    const cardType =
      (purchaseData as any)?.data?.purchase?.payment?.mopInfo?.card?.cardType || ''
    const currencyCode = (purchaseData as any)?.data?.order?.currency_code || 'USD'
    const subtotalAmount = (purchaseData as any)?.data?.order?.subtotal
    const taxAmount = (purchaseData as any)?.data?.order?.tax_paid
    const totalAmount = (purchaseData as any)?.data?.order?.total
    const productId = (purchaseData as any)?.data?.products?.[0]?.product_id || ''
    const productType = (purchaseData as any)?.data?.products?.[0]?.product_type || ''
    const couponCode = (purchaseData as any)?.data?.purchase?.coupon_code || ''
    const couponDiscountAmount =
      (purchaseData as any)?.data?.purchase?.coupon_discount_amount || 0

    trackAmplitudeEvent({
      type: 'purchase_completed',
      properties: {
        order_id: orderId,
        payment_method: paymentMethod,
        card_type: cardType,
        currency_code: currencyCode,
        subtotal_amount: subtotalAmount,
        tax_amount: taxAmount,
        total_amount: totalAmount,
        product_id: productId,
        product_type: productType,
        coupon_code: couponCode,
        coupon_discount_amount: couponDiscountAmount,
      },
    })
  } catch (error) {
    // Silently handle errors to avoid breaking the payment flow
  }
}

/**
 * Track paper upload processed event
 * @param paperUploadId - The ID of the uploaded paper
 * @param pageUrl - The current page URL
 */
export const trackPaperUploadProcessed = (
  paperUploadId: string,
  pageUrl?: string
): void => {
  trackAmplitudeEvent({
    type: 'state_confirmed',
    properties: {
      name: 'paper upload processed',
      state: 'success',
      page_url:
        pageUrl ||
        (typeof window !== 'undefined' && window.location ? window.location.href : ''),
      paper_upload_id: paperUploadId,
    },
  })
}

export enum ElementType {
  BUTTON = 'button',
  LINK = 'link',
  INPUT = 'input',
  SELECT = 'select',
  CHECKBOX = 'checkbox',
  RADIO = 'radio',
}
